/** /notifications/features
 *
 * Cross-tenant notification feature-flag grid, driven by the reusable
 * QuerySearch widget (same `?q=` contract as /notifications/ and /bookings/).
 * Rows are SPARSE overrides — absence of a row means the feature is enabled —
 * so the grid shows explicit overrides only; "New flag" creates one.
 *
 * `enabled` toggles inline from the grid (no drawer). Batch ops: enable /
 * disable with an OPTIONAL comment (recorded on the rows). No delete anywhere
 * — revert-to-default = toggle enabled back on. "Validate…" opens the
 * per-level evaluation tool.
 *
 * No standalone TenantPicker here (deliberate): most rows are NOT
 * tenant-scoped (system/OTA levels), and a hard tenant lock would hide the
 * system-level rows that usually answer "why is this feature off for tenant
 * X". Tenant filtering goes through the `tenant` namespace.
 */
import AddIcon from '@mui/icons-material/Add';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { BatchSelection } from '@/api/batch';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import * as nffApi from '@/api/notificationFeatures';
import { type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { CreateFeatureFlagDialog } from './CreateFeatureFlagDialog';
import { EditTagFlagDialog } from './EditTagFlagDialog';
import { buildColumns } from './notificationFeatureFlagsColumns';
import { buildFeatureFlagNamespaces } from './notificationFeatureFlagsNamespaces';
import { ValidateFeatureDialog } from './ValidateFeatureDialog';

const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_HIDDEN = ['id', 'created_ts', 'updated_ip'];

/** Presets store the filter state as the raw `?q=` DSL text (canonical form). */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

export function NotificationFeatureFlagsPage() {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [sp, setSp] = useSearchParams();
  const apiRef = useGridApiRef();
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));
  const [validateOpen, setValidateOpen] = useState(false);
  // Tag rows are the one editable-scope exception — the pencil opens this.
  const [editRow, setEditRow] = useState<nffApi.FeatureFlagListItem | null>(null);

  const registry = useQuery({
    queryKey: ['notification-features', 'registry'],
    queryFn: nffApi.getRegistry,
    staleTime: 5 * 60_000,
  });
  const featureDefs = useMemo(() => registry.data?.features ?? [], [registry.data?.features]);
  const featureNames = useMemo(
    () => new Map(featureDefs.map((f) => [f.codename, f.name])),
    [featureDefs],
  );

  const namespaces = useMemo(() => buildFeatureFlagNamespaces(featureDefs), [featureDefs]);

  const presets = useGridPresets({
    grid: 'notification_feature_flags',
    defaultOrder: useMemo(
      () =>
        buildColumns({
          featureNames: new Map(),
          canManage: false,
          togglePending: false,
          onToggle: () => undefined,
          onEditTagRow: () => undefined,
        }).map((c) => c.field),
      [],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'feature',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'notification_feature_flags' });
  const query = useQuery({
    queryKey: [
      'notification-feature-flags',
      pagination.page,
      pagination.pageSize,
      ordering,
      q,
      extendedTimeout,
    ],
    queryFn: () =>
      nffApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        filters: toWireAst(tree),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking,
  });

  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);
  const rowCount = query.data?.count ?? 0;

  // ------------------------------------------------------------------------
  // Inline enabled toggle — mutate then invalidate (no optimistic update).
  // ------------------------------------------------------------------------
  const canManage = hasPerm('console_notification_features_manage');
  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      nffApi.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-feature-flags'] }),
    onError: (e) =>
      snackbar.show(
        e instanceof ConsoleApiError ? e.message : 'Could not update the flag.',
        'error',
      ),
  });
  const onToggle = useCallback(
    (id: number, enabled: boolean) => toggleMutation.mutate({ id, enabled }),
    [toggleMutation],
  );

  // ------------------------------------------------------------------------
  // Batch enable/disable — hand-wired (raw DataGridPro), integer row ids.
  // ------------------------------------------------------------------------
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  const queryIdentity = useMemo(() => JSON.stringify({ q, ordering }), [q, ordering]);
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  const selectedIds = useMemo(() => selectionModel.map((i) => Number(i)), [selectionModel]);
  const selectedCount = allMatching ? rowCount : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    const wire = toWireAst(tree);
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tree]);

  const batchAction = useBatchAction({
    resource: 'notification_feature_flags',
    queryKey: ['notification-feature-flags'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  // Declared unconditionally; BatchActionsMenu filters by `permission`.
  // Deliberately NO delete action (revert = enable back on).
  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'enable',
        label: 'Enable',
        action: 'enable',
        permission: 'console_notification_features_manage',
        reason: 'optional',
        reasonParam: 'comment',
        reasonLabel: 'Comment (optional)',
        confirmText: (n) =>
          `Enable ${n.toLocaleString()} feature flag(s)? Gated blocks resume rendering for those scopes. Runs in the background.`,
      },
      {
        key: 'disable',
        label: 'Disable',
        action: 'disable',
        permission: 'console_notification_features_manage',
        destructive: true,
        reason: 'optional',
        reasonParam: 'comment',
        reasonLabel: 'Comment (optional)',
        confirmText: (n) =>
          `Disable ${n.toLocaleString()} feature flag(s)? Gated blocks stop rendering immediately for those scopes. Runs in the background.`,
      },
    ],
    [],
  );

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({
          featureNames,
          canManage,
          togglePending: toggleMutation.isPending,
          onToggle,
          onEditTagRow: setEditRow,
        }),
        presets.orderedFields,
      ),
    [featureNames, canManage, toggleMutation.isPending, onToggle, presets.orderedFields],
  );

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="notification-feature-flags"
        title="Notification Feature Flags"
        subtitle="Leveled overrides gating feature blocks in notifications. No row = enabled; rows override per system / OTA / tenant / tour / notification — or require tags to be present (tag level)."
        actions={
          <Stack direction="row" spacing={1}>
            <PermissionGate perm="console_notification_features_view">
              <Button
                variant="outlined"
                startIcon={<FactCheckIcon />}
                onClick={() => setValidateOpen(true)}
              >
                Validate…
              </Button>
            </PermissionGate>
            <PermissionGate perm="console_notification_features_manage">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New flag
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      <Box sx={{ mb: 2 }}>
        <QuerySearchInput
          namespaces={namespaces}
          value={tree}
          onChange={setQuery}
          placeholder="Search flags…  e.g. feature=pickup & level=tenant & enabled=no"
        />
      </Box>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<nffApi.FeatureFlagListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={rowCount}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnFilter
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          keepNonExistentRowsSelected
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch
                  ? 'No flags match this search.'
                  : 'No overrides yet — every feature is enabled by default. "New flag" creates an override.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
              hideFilterButton: true,
              exportFileName: 'notification_feature_flags',
              presets: presets.toolbar,
              batch: {
                actions: batchActions,
                ids: selectedIds,
                allMatching,
                count: selectedCount,
                matched: rowCount,
                filterQuery,
                runStandard,
                clearSelection,
                onSelectAllMatching: () => setAllMatching(true),
                isPending: batchAction.isPending,
              },
            } as GridToolbarProps,
          }}
        />
      )}

      {createOpen && (
        <CreateFeatureFlagDialog
          open
          featureDefs={featureDefs}
          levels={registry.data?.levels ?? []}
          tagSources={registry.data?.tag_sources ?? []}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editRow && (
        <EditTagFlagDialog
          open
          row={editRow}
          tagSources={registry.data?.tag_sources ?? []}
          onClose={() => setEditRow(null)}
        />
      )}
      {validateOpen && (
        <ValidateFeatureDialog
          open
          featureDefs={featureDefs}
          onClose={() => setValidateOpen(false)}
        />
      )}
    </Box>
  );
}
